import { auth } from "../config/firebase";
import { API_BASE } from "../config/constants";
import { ApiResponse } from "../types";

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const authHeaders = await getAuthHeader();

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
      ...(options.headers ?? {}),
    },
  });

  const data: ApiResponse<T> = await res.json();

  if (!res.ok) {
    throw new Error(data.error ?? `Request failed with status ${res.status}`);
  }

  return data;
}

// Unauthenticated request — no Firebase token (used by the guest join page)
async function guestRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers ?? {}),
    },
  });

  const data: ApiResponse<T> = await res.json();

  if (!res.ok) {
    throw new Error(data.error ?? `Request failed with status ${res.status}`);
  }

  return data;
}

// ── User ──────────────────────────────────────────────────────────────────────

export const api = {
  users: {
    me: () => request("/users/me"),
    ensureProfile: (body: object) =>
      request("/users/me", { method: "POST", body: JSON.stringify(body) }),
    updateProfile: (body: object) =>
      request("/users/me", { method: "PATCH", body: JSON.stringify(body) }),
    search: (q: string) => request(`/users/search?q=${encodeURIComponent(q)}`),
    setLookingForGame: (isLooking: boolean) =>
      request("/users/me/looking-for-game", {
        method: "PATCH",
        body: JSON.stringify({ isLooking }),
      }),
    getLookingForGame: () => request("/users/looking-for-game"),
  },

  friends: {
    list: () => request("/friends"),
    pending: () => request("/friends/pending"),
    sent: () => request("/friends/sent"),
    sendRequest: (toUserId: string) =>
      request("/friends/request", {
        method: "POST",
        body: JSON.stringify({ toUserId }),
      }),
    accept: (id: string) =>
      request(`/friends/${id}/accept`, { method: "PATCH" }),
    decline: (id: string) =>
      request(`/friends/${id}/decline`, { method: "PATCH" }),
    remove: (id: string) => request(`/friends/${id}`, { method: "DELETE" }),
  },

  groups: {
    list: () => request("/groups"),
    get: (id: string) => request(`/groups/${id}`),
    create: (body: object) =>
      request("/groups", { method: "POST", body: JSON.stringify(body) }),
    acceptInvite: (id: string) =>
      request(`/groups/${id}/accept`, { method: "PATCH" }),
    leave: (id: string) => request(`/groups/${id}/leave`, { method: "DELETE" }),
  },

  events: {
    list: () => request("/events"),
    get: (id: string) => request(`/events/${id}`),
    create: (body: object) =>
      request("/events", { method: "POST", body: JSON.stringify(body) }),
    updateStatus: (id: string, status: string) =>
      request(`/events/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    respond: (id: string, accept: boolean) =>
      request(`/events/${id}/respond`, {
        method: "PATCH",
        body: JSON.stringify({ accept }),
      }),
    invitations: () => request("/events/invitations"),
    update: (
      id: string,
      body: { name?: string; notes?: string; roundDate?: string },
    ) =>
      request(`/events/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    delete: (id: string) => request(`/events/${id}`, { method: "DELETE" }),
  },

  pariMutuel: {
    getByEventId: (eventId: string) =>
      request(`/parimutuel/by-event/${eventId}`),
    get: (id: string) => request(`/parimutuel/${id}`),
    create: (body: object) =>
      request("/parimutuel", { method: "POST", body: JSON.stringify(body) }),
    addBet: (id: string, body: object) =>
      request(`/parimutuel/${id}/bets`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateBet: (id: string, betId: string, body: object) =>
      request(`/parimutuel/${id}/bets/${betId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    removeBet: (id: string, betId: string) =>
      request(`/parimutuel/${id}/bets/${betId}`, { method: "DELETE" }),
    lock: (id: string) =>
      request(`/parimutuel/${id}/lock`, { method: "PATCH" }),
    open: (id: string) =>
      request(`/parimutuel/${id}/open`, { method: "PATCH" }),
    close: (id: string) =>
      request(`/parimutuel/${id}/close`, { method: "PATCH" }),
    unlock: (id: string) =>
      request(`/parimutuel/${id}/unlock`, { method: "PATCH" }),
    saveResults: (id: string, body: object) =>
      request(`/parimutuel/${id}/results`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    updateSettings: (
      id: string,
      body: {
        minBet?: number | null;
        maxBet?: number | null;
        showAllBets?: boolean;
      },
    ) =>
      request(`/parimutuel/${id}/settings`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    settle: (id: string, body: object) =>
      request(`/parimutuel/${id}/settle`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    markSettled: (id: string) =>
      request(`/parimutuel/${id}/mark-settled`, { method: "PATCH" }),
    unsettle: (id: string) =>
      request(`/parimutuel/${id}/unsettle`, { method: "PATCH" }),
    addParticipant: (id: string, body: { name: string }) =>
      request(`/parimutuel/${id}/participants`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    removeParticipant: (id: string, participantId: string) =>
      request(`/parimutuel/${id}/participants/${participantId}`, {
        method: "DELETE",
      }),
    scratchParticipant: (
      id: string,
      participantId: string,
      scratched: boolean,
    ) =>
      request(`/parimutuel/${id}/participants/${participantId}/scratch`, {
        method: "PATCH",
        body: JSON.stringify({ scratched }),
      }),
    reconcile: (id: string, numWinners?: number) =>
      request(`/parimutuel/${id}/reconcile`, {
        method: "PATCH",
        body: JSON.stringify({ numWinners: numWinners ?? 2 }),
      }),
    markBetPaid: (id: string, betId: string, paid: boolean) =>
      request(`/parimutuel/${id}/bets/${betId}/paid`, {
        method: "PATCH",
        body: JSON.stringify({ paid }),
      }),
  },

  join: {
    getEvent: (shareCode: string) =>
      guestRequest(`/parimutuel/join/${shareCode}`),
    getMyBets: (shareCode: string, deviceId: string) =>
      guestRequest(
        `/parimutuel/join/${shareCode}/bets?deviceId=${encodeURIComponent(deviceId)}`,
      ),
    getAllBets: (shareCode: string) =>
      guestRequest(`/parimutuel/join/${shareCode}/all-bets`),
    submitBets: (
      shareCode: string,
      body: {
        bettorName: string;
        deviceId?: string;
        bets: Array<{
          participantId: string;
          winAmount: number;
          placeAmount: number;
          showAmount: number;
        }>;
      },
    ) =>
      guestRequest(`/parimutuel/join/${shareCode}/bets`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
};
